import { KeyboardEvent, MouseEvent, useCallback, useState } from 'react';
import styled from 'styled-components';
import { MarkdownViewer } from '@ardoq/markdown';
import { getFocusRingBorderCSS } from '@ardoq/style-helpers';

export const ZoomableMarkdownViewer = styled(MarkdownViewer)`
  img[role='button'] {
    cursor: zoom-in;

    &:focus-visible {
      ${getFocusRingBorderCSS()};
    }
  }
`;

const IMAGE_TAG_NAME = 'IMG';

/**
 * Images inside a link keep the link's behaviour, so they must not also be
 * announced as buttons.
 */
const isZoomable = (image: Element): boolean => image.closest('a') === null;

const makeImagesFocusable = (
  sanitizedHTML: string,
  zoomButtonLabel: string
): string => {
  const document = new DOMParser().parseFromString(sanitizedHTML, 'text/html');
  document.querySelectorAll('img').forEach(image => {
    if (!isZoomable(image)) return;
    image.setAttribute('tabindex', '0');
    image.setAttribute('role', 'button');
    image.setAttribute('aria-label', image.alt || zoomButtonLabel);
  });
  return document.body.innerHTML;
};

const resolveZoomableImage = (target: EventTarget | null): string | null => {
  if (!(target instanceof Element) || target.tagName !== IMAGE_TAG_NAME) {
    return null;
  }
  if (!isZoomable(target)) return null;
  const image = target as HTMLImageElement;
  return image.currentSrc || image.src || null;
};

export type MarkdownImagePreviewViewerProps = {
  transformHTML: (sanitizedHTML: string) => string;
  onClick: (event: MouseEvent<HTMLDivElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
};

/**
 * Keeps the preview state outside the markdown viewer, so that a caller
 * rendering the viewer inside a popover can still mount the preview modal
 * elsewhere. The modal portals to the document body, and a popover treats a
 * click in the portal as a click outside itself, which would close the popover
 * and unmount the modal mid-interaction.
 */
export const useMarkdownImagePreview = (zoomButtonLabel: string) => {
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  const transformHTML = useCallback(
    (sanitizedHTML: string) =>
      makeImagesFocusable(sanitizedHTML, zoomButtonLabel),
    [zoomButtonLabel]
  );

  const onClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const src = resolveZoomableImage(event.target);
    if (src) setPreviewImageUrl(src);
  }, []);

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const src = resolveZoomableImage(event.target);
    if (!src) return;
    event.preventDefault();
    setPreviewImageUrl(src);
  }, []);

  const closePreview = useCallback(() => setPreviewImageUrl(null), []);

  return {
    viewerProps: { transformHTML, onClick, onKeyDown },
    previewImageUrl,
    closePreview,
  };
};
